<template @keydown.native="keydown">
  <section class="builder">
    <h1>{{$t('builder.title')}}</h1>
    <vs-row vs-justify="center" class="topform">
      <vs-input
        :label="$t('catalog.name')"
        :placeholder="$t('catalog.name_placeholder')"
        type="text"
        v-model="catalog.title"
      />
      <vs-select
        autocomplete
        @input-change="searchCategory"
        :label="$t('catalog.categories')"
        v-model="catalog.category"
      >
        <vs-select-item :key="c_idx" :value="c.id" :text="c.name" v-for="(c,c_idx) in categories"/>
      </vs-select>
      <vs-select
        autocomplete
        @input-change="searchTheme"
        :label="$t('catalog.themes')"
        v-model="catalog.theme"
      >
        <vs-select-item :key="t_idx" :value="t.id" :text="t.title" v-for="(t,t_idx) in themes"/>
      </vs-select>
      <div class="datepicker">
        <span>{{ $t('catalog.exp_date') }}</span>
        <datepicker
          :placeholder="$t('catalog.exp_date_placeholder')"
          class="datepicker-input"
          v-model="catalog.expired_at"
        ></datepicker>
      </div>
    </vs-row>
    <vs-row vs-justify="center">
      <div
        class="img-preview"
        v-if="catalog.avatar && catalog.avatar.length > 0"
        :style="`background-image: url('${catalog.avatar}');`"
      >
        <vs-button color="danger" type="filled" icon="delete" @click="deleteAvatar()"></vs-button>
      </div>
      <vs-upload
        :single-upload="true"
        class="uploader"
        fileName="avatar"
        text="Load avatar"
        limit="1"
        automatic
        :headers="headers"
        :action="getUploadAPI()"
        @on-success="avatarUploaded"
      />
    </vs-row>
    <vs-row vs-justify="center" class="topform-sub">
      <vs-button color="primary" @click="submit">{{$t('common.save')}}</vs-button>
      <vs-button color="success" v-if="catalog.id" @click="view">{{$t('common.view')}}</vs-button>
    </vs-row>
    <vs-row>
      <vs-col vs-type="flex" vs-justify="center" vs-align="center" vs-w="6">
        <draggable
          class="draggable source"
          v-model="tools"
          :group="{ name: 'tool', pull: 'clone', put: false }"
          @start="drag=true"
          @end="drag=false"
        >
          <div v-for="tool in tools" :key="tool.id">
            <vs-card>
              <div slot="header" class="card-head">
                <h3>{{tool.name}}</h3>
                <span>{{tool.description}}</span>
              </div>
              <div>
                <img class="example-img" :src="getExampleImage(tool.type)">
              </div>
              <div slot="footer">
                <vs-row vs-justify="flex-end">
                  <vs-button color="primary" icon="playlist_add" @click="add(tool)"></vs-button>
                </vs-row>
              </div>
            </vs-card>
          </div>
        </draggable>
      </vs-col>
      <vs-col vs-type="flex" vs-justify="center" class="elements" vs-align="center" vs-w="6">
        <draggable
          class="draggable target"
          :group="{ name: 'details', pull: 'clone', put: false }"
          v-model="catalog.details"
          @start="drag=true"
          @end="drag=false"
        >
          <div v-for="(element, index) in catalog.details" :key="index">
            <vs-card>
              <div slot="header" class="card-head">
                <h3>{{element.name}}</h3>
                <span>{{element.description}}</span>
              </div>
              <div v-if="element.form">
                <template v-for="(input, idx) in element.form">
                  <vs-row :key="idx" vs-justify="center">
                    <vs-input
                      class="element-input"
                      v-if="input.type != 'image'"
                      :type="input.type"
                      :key="idx"
                      :placeholder="getTranslation(input.name)"
                      v-model="input.value"
                    />

                    <div
                      class="img-preview"
                      v-if="input.type == 'image' && input.value.length"
                      :style="`background-image: url('${input.value}');`"
                    >
                      <vs-button
                        color="danger"
                        type="filled"
                        icon="delete"
                        @click="deleteImage(input)"
                      ></vs-button>
                    </div>
                    <vs-upload
                      v-if="input.type == 'image'"
                      :key="idx"
                      :single-upload="true"
                      class="uploader"
                      automatic
                      fileName="avatar"
                      :text="`${$t('common.uploading')} ${getTranslation(input.name)}`"
                      limit="1"
                      :headers="headers"
                      :action="getUploadAPI()"
                      @on-success="(event) => imageUploaded(event, input)"
                    />
                  </vs-row>
                </template>
              </div>
              <div slot="footer">
                <vs-row vs-justify="flex-end">
                  <vs-button color="danger" icon="delete" @click="remove(index)"></vs-button>
                </vs-row>
              </div>
            </vs-card>
          </div>
        </draggable>
      </vs-col>
    </vs-row>
    <vs-row vs-justify="center" class="save">
      <vs-button color="primary" @click="submit">{{$t('common.save')}}</vs-button>
    </vs-row>
  </section>
</template>

<script>
const { VUE_APP_API_HOST, VUE_APP_VIEWER_HOST } = process.env

import Datepicker from 'vuejs-datepicker'
import draggable from 'vuedraggable'

import Theme from '../mixins/Themes.vue'
import Catalog from '../mixins/Catalogs.vue'
import Category from '../mixins/Categories.vue'

import Tools from '../data/tools'

export default {
  name: 'builder',

  mixins: [Theme, Catalog, Category],

  props: {
    catalog: {
      type: Object,
      default: () => ({
        title: '',
        theme: '',
        avatar: '',
        category: '',
        details: []
      })
    }
  },

  components: {
    Datepicker,
    draggable
  },

  data: () => ({
    headers: {
      Authorization: `Bearer ${localStorage.getItem('user-token')}`
    },
    themes: [],
    categories: [],
    tools: [].concat(Tools)
  }),

  watch: {
    'catalog.details'() {
      this.tools = [].concat(Tools)
    }
  },

  beforeMount() {
    this.$vs.loading({ color: 'primary' })
    this.listThemes().then(res => {
      if (!res.body || !res.body.success) {
        this.$vs.loading.close()
        return this.$vs.dialog({
          color: 'danger',
          title: this.$t('common.error.title'),
          text: this.$t('common.error.text')
        })
      }

      this.themes = res.body.themes
      this.$vs.loading.close()
    })
    this.listCategories().then(res => {
      if (!res.body || !res.body.success) {
        this.$vs.loading.close()
        return this.$vs.dialog({
          color: 'danger',
          title: this.$t('common.error.title'),
          text: this.$t('common.error.text')
        })
      }

      this.categories = res.body.categories
      this.$vs.loading.close()
    })

    window.addEventListener('keydown', event => {
      if ((event.ctrlKey || event.metaKey) && event.which == 83) {
        this.submit()
        event.preventDefault()
        return false
      }
    })
  },

  methods: {
    getUploadAPI() {
      return `${VUE_APP_API_HOST}/catalogs/upload`
    },

    getExampleImage(type) {
      return require(`../assets/img/examples/${type}.jpg`)
    },

    getTranslation(name) {
      return this.$t(`tools.inputs.${name}`)
    },

    add(tool) {
      this.catalog.details.push(tool)
    },
    remove(index) {
      this.catalog.details.splice(index, 1)
    },
    searchCategory() {},
    searchTheme() {},
    deleteAvatar() {
      this.catalog.avatar = ''
      this.update()
    },
    avatarUploaded(event) {
      const body = JSON.parse(event.currentTarget.response)
      this.catalog.avatar = body.image
      this.update()
    },
    imageUploaded(event, input) {
      const body = JSON.parse(event.currentTarget.response)
      input.value = body.image
    },
    deleteImage(input) {
      this.$vs.dialog({
        type: 'confirm',
        color: 'danger',
        title: this.$t('common.warn.sure_ttl'),
        text: this.$t('common.warn.sure_text'),
        accept: () => {
          input.value = ''
        }
      })
    },
    view() {
      window.open(
        `${VUE_APP_VIEWER_HOST}/viewer/catalogs/${this.catalog.id}`,
        '_blank'
      )
    },
    submit() {
      if (this.catalog.id) {
        this.update()
      } else {
        this.save()
      }
    },
    update() {
      this.$vs.loading({ color: 'primary' })
      this.updateCatalog(this.catalog).then(res => {
        if (!res.body || !res.body.success) {
          this.$vs.loading.close()
          return this.$vs.dialog({
            color: 'danger',
            title: this.$t('common.error.title'),
            text: this.$t('common.error.text')
          })
        }

        this.$vs.notify({
          title: this.$t('common.success.title'),
          text: this.$t('common.success.text'),
          color: 'success'
        })

        setTimeout(this.$vs.loading.close, 1000)
      })
    },

    save() {
      this.$vs.loading({ color: 'primary' })
      this.saveCatalog(this.catalog).then(res => {
        if (!res.body || !res.body.success) {
          this.$vs.loading.close()
          return this.$vs.dialog({
            color: 'danger',
            title: this.$t('common.error.title'),
            text: this.$t('common.error.text')
          })
        }

        this.$vs.notify({
          title: this.$t('common.success.title'),
          text: this.$t('common.success.text'),
          color: 'success'
        })

        this.catalog = res.body.catalog
        setTimeout(this.$vs.loading.close, 1000)
      })
    }
  }
}
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped lang="scss">
.datepicker {
  text-align: center;
  width: 200px;
  span {
    padding-left: 5px;
    font-size: 0.85rem;
    color: rgba(0, 0, 0, 0.6);
  }
  .datepicker-input {
    border: 1px solid rgba(0, 0, 0, 0.2);
    border-radius: 5px;
    padding: 0.15rem;
    padding-left: 0.55rem;
  }
}
.con-vs-card {
  width: 90%;
  margin: 20px auto;
}
.topform,
.topform-sub {
  margin: 20px auto;
  & > div {
    margin: 0 10px;
  }
}
.uploader {
  width: 200px;
  margin: 0 100%;
}
.img-preview {
  width: 100px;
  height: 100px;
  background: #2c1d71;
  background-size: cover;
  background-repeat: no-repeat;
  border-radius: 10px;
  margin: 20px;
  display: inline-block;
}
.example-img {
  width: 100%;
  border-radius: 10px;
}
.card-head {
  cursor: move;
  span {
    font-size: 12px;
  }
}
.element-input {
  margin: 5px;
  input {
    padding: 10px;
  }
}
.save {
  margin: 20px auto;
}
.builder {
  .draggable {
    min-height: 100%;
    min-width: 100%;

    &.target {
      background-image: linear-gradient(
        136deg,
        #170c4f 25%,
        #6552c4 25%,
        #6552c4 50%,
        #170c4f 50%,
        #170c4f 75%,
        #6552c4 75%,
        #6552c4 100%
      );
      background-size: 57.58px 55.61px;
      border-top-left-radius: 10px;
      border-bottom-left-radius: 10px;
    }
  }
}
</style>
